// DNA Pay - Configurações de Gateways
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DnaTableV4 } from '@/design-system/DnaDesignSystem';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Settings, CreditCard, QrCode, Barcode, Building,
  CheckCircle, XCircle, Loader2, Plus, Edit, Save,
  AlertTriangle, ExternalLink, Eye, EyeOff, Zap, Activity, FileBarChart
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const gatewayInfo = {
  mercadopago: {
    name: 'MercadoPago',
    icon: QrCode,
    color: 'text-green-600 bg-green-100',
    description: 'PIX instantâneo via MercadoPago',
    fields: ['access_token'],
    docs: 'https://www.mercadopago.com.br/developers'
  },
  pagbank: {
    name: 'PagBank',
    icon: CreditCard,
    color: 'text-purple-600 bg-purple-100',
    description: 'Cartão de crédito e débito',
    fields: ['client_id', 'client_secret'],
    docs: 'https://dev.pagseguro.uol.com.br/'
  },
  safra: {
    name: 'Banco Safra',
    icon: Building,
    color: 'text-blue-600 bg-blue-100',
    description: 'Boletos bancários registrados',
    fields: ['api_key', 'conta_banco', 'agencia', 'convenio', 'carteira'],
    docs: 'https://safra.com.br/empresas'
  }
};

// ─── Componente: Configuração detalhada Banco Safra ───────────────────
function SafraConfigForm({ configForm, setConfigForm, onTestar, testando, statusEmissao }) {
  const [showSecret, setShowSecret] = React.useState({});
  const set = (k, v) => setConfigForm(prev => ({ ...prev, [k]: v }));
  const toggleSecret = (k) => setShowSecret(s => ({ ...s, [k]: !s[k] }));

  const SecretInput = ({ name, label, placeholder }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <Input
          type={showSecret[name] ? 'text' : 'password'}
          value={configForm[name] || ''}
          onChange={(e) => set(name, e.target.value)}
          placeholder={placeholder || label}
          className="pr-9"
          data-testid={`safra-${name}`}
        />
        <button
          type="button"
          onClick={() => toggleSecret(name)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          aria-label="Ver/ocultar"
        >
          {showSecret[name] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  const Field = ({ name, label, type = 'text', placeholder, step }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        step={step}
        value={configForm[name] ?? ''}
        onChange={(e) => set(name, type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
        placeholder={placeholder || label}
        data-testid={`safra-${name}`}
      />
    </div>
  );

  return (
    <Tabs defaultValue="padroes" className="space-y-3">
      <TabsList className="bg-purple-50 border border-purple-100">
        <TabsTrigger value="padroes" className="data-[state=active]:bg-purple-700 data-[state=active]:text-white">Configurações Padrões</TabsTrigger>
        <TabsTrigger value="campos" className="data-[state=active]:bg-purple-700 data-[state=active]:text-white">Campos Configuráveis</TabsTrigger>
        <TabsTrigger value="cnab" className="data-[state=active]:bg-purple-700 data-[state=active]:text-white">Mapeamento CNAB</TabsTrigger>
      </TabsList>

      {/* Aba: PADRÕES */}
      <TabsContent value="padroes" className="space-y-4">
        <Card className="border-purple-100">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-purple-800"><Zap className="w-4 h-4" />Ambiente & API</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Gateway Ativo</Label>
              <Switch checked={configForm.ativo || false} onCheckedChange={(c) => set('ativo', c)} data-testid="safra-ativo" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Ambiente</Label>
                <Select value={configForm.ambiente || 'sandbox'} onValueChange={(v) => set('ambiente', v)}>
                  <SelectTrigger data-testid="safra-ambiente"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Homologação</SelectItem>
                    <SelectItem value="production">Produção</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field name="api_base_url" label="API Base URL" placeholder="https://api.safra.com.br/..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field name="client_id" label="Client ID" />
              <SecretInput name="client_secret" label="Client Secret" />
            </div>
            <SecretInput name="webhook_secret" label="Webhook Secret" />
          </CardContent>
        </Card>

        <Card className="border-emerald-100">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-emerald-800"><Activity className="w-4 h-4" />Status da Conexão</CardTitle></CardHeader>
          <CardContent className="text-xs space-y-1.5">
            <div className="flex justify-between"><span className="text-slate-500">Última conexão testada:</span><span className="font-medium">{configForm.ultima_conexao_teste || '—'}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Status conexão:</span><Badge className={configForm.ultima_conexao_status === 'ok' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>{configForm.ultima_conexao_status || 'não testado'}</Badge></div>
            <div className="flex justify-between"><span className="text-slate-500">Última sincronização:</span><span className="font-medium">{configForm.ultima_sincronizacao || '—'}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Última remessa:</span><span className="font-medium">{configForm.ultima_remessa || '—'}</span></div>
            <Button size="sm" onClick={onTestar} disabled={testando} className="w-full mt-2 bg-gradient-to-r from-purple-700 to-emerald-600 text-white" data-testid="safra-testar-conexao">
              {testando ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Testando…</> : <><Zap className="w-4 h-4 mr-1" />Testar conexão</>}
            </Button>
          </CardContent>
        </Card>

        {statusEmissao && (
          <Card className="border-amber-100">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-amber-800"><FileBarChart className="w-4 h-4" />Status da Emissão (hoje)</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="rounded-md border bg-slate-50 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Emitidos</div>
                  <div className="text-lg font-bold text-slate-900">{statusEmissao.boletos_emitidos_hoje ?? 0}</div>
                </div>
                <div className="rounded-md border bg-emerald-50 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-700">Registrados</div>
                  <div className="text-lg font-bold text-emerald-700">{statusEmissao.boletos_registrados ?? 0}</div>
                </div>
                <div className="rounded-md border bg-amber-50 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-amber-700">Pendentes</div>
                  <div className="text-lg font-bold text-amber-700">{statusEmissao.boletos_pendentes ?? 0}</div>
                </div>
                <div className="rounded-md border bg-rose-50 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-rose-700">Rejeitados</div>
                  <div className="text-lg font-bold text-rose-700">{statusEmissao.boletos_rejeitados ?? 0}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      {/* Aba: CAMPOS CONFIGURÁVEIS */}
      <TabsContent value="campos" className="space-y-3">
        <Card className="border-purple-100">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-purple-800">Dados Bancários</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field name="agencia" label="Agência" />
            <Field name="conta_banco" label="Conta" />
            <Field name="carteira" label="Carteira" />
            <Field name="convenio" label="Convênio" />
            <Field name="codigo_beneficiario" label="Código Beneficiário" />
          </CardContent>
        </Card>

        <Card className="border-purple-100">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-purple-800">Dados da Empresa</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <Field name="razao_social" label="Razão Social" />
            <Field name="nome_fantasia" label="Nome Fantasia" />
            <Field name="cnpj" label="CNPJ" placeholder="00.000.000/0000-00" />
          </CardContent>
        </Card>

        <Card className="border-purple-100">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-purple-800">Instruções do Boleto</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Field name="local_pagamento_antes" label="Local pgto. antes do vencimento" placeholder="Pagável em qualquer banco até o vencimento" />
            <Field name="local_pagamento_apos" label="Local pgto. após vencimento" placeholder="Pagável somente na rede Safra após o vencimento" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field name="juros_percentual" label="Juros (% a.m.)" type="number" step="0.01" />
              <Field name="multa_percentual" label="Multa (%)" type="number" step="0.01" />
              <Field name="dias_protesto" label="Dias para protesto" type="number" />
              <Field name="dias_negativacao" label="Dias para negativação" type="number" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-100">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-800">PIX no Boleto</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 items-end">
            <Field name="pix_chave" label="Chave PIX" placeholder="cnpj/email/telefone/aleatória" />
            <div className="flex items-center justify-between border rounded-md px-3 py-2">
              <Label className="text-xs">Habilitar PIX</Label>
              <Switch checked={configForm.pix_habilitado || false} onCheckedChange={(c) => set('pix_habilitado', c)} data-testid="safra-pix-habilitado" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-purple-100">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-purple-800">Layout / Sequencial</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-3 gap-3">
            <Field name="proximo_nosso_numero" label="Próximo nosso número" type="number" />
            <Field name="nosso_numero_inicial" label="Nosso número inicial" type="number" />
            <Field name="nosso_numero_tamanho" label="Tamanho nosso número" type="number" />
          </CardContent>
        </Card>

        <Card className="border-emerald-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-emerald-800">CNAB 400 — Layout Posicional (Homologação Safra)</CardTitle>
            <CardDescription className="text-xs">
              Campos que vão direto para as posições do arquivo de remessa. Se em branco, usa o padrão homologado.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field name="agencia_depositaria" label="Agência depositária (pos 143-147)" placeholder="ex: 19800" />
            <Field name="especie_padrao" label="Espécie padrão (pos 148-149)" placeholder="01 (DM)" />
            <Field name="aceite_padrao" label="Aceite padrão (pos 150)" placeholder="N" />
            <Field name="instrucao_1" label="Instrução 1 (pos 157-158)" placeholder="ex: 06" />
            <Field name="instrucao_2" label="Instrução 2 (pos 159-160)" placeholder="ex: 09" />
          </CardContent>
        </Card>
      </TabsContent>

      {/* Aba: CNAB */}
      <TabsContent value="cnab" className="space-y-3">
        <Card className="border-purple-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-purple-800">Mapeamento CNAB / Retorno Bancário</CardTitle>
            <CardDescription className="text-xs">
              Configure o layout e leitura automática dos arquivos retorno.
              Campos lidos no retorno: <b>número boleto</b>, <b>data pagamento</b>, <b>valor pago</b>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Layout CNAB</Label>
                <Select value={configForm.cnab_layout || '240'} onValueChange={(v) => set('cnab_layout', v)}>
                  <SelectTrigger data-testid="safra-cnab-layout"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="240">CNAB 240</SelectItem>
                    <SelectItem value="400">CNAB 400</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Encoding</Label>
                <Select value={configForm.cnab_encoding || 'ISO-8859-1'} onValueChange={(v) => set('cnab_encoding', v)}>
                  <SelectTrigger data-testid="safra-cnab-encoding"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ISO-8859-1">ISO-8859-1 (Latin1)</SelectItem>
                    <SelectItem value="UTF-8">UTF-8</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field name="cnab_separador" label="Separador" placeholder="padrão CNAB" />
            </div>
            <div className="flex items-center justify-between border rounded-md px-3 py-2 bg-emerald-50/40">
              <div>
                <Label>Leitura automática do retorno</Label>
                <p className="text-xs text-slate-500">Processa arquivos retorno baixados via webhook/SFTP.</p>
              </div>
              <Switch checked={configForm.leitura_automatica_retorno || false} onCheckedChange={(c) => set('leitura_automatica_retorno', c)} data-testid="safra-leitura-auto" />
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}

// ─── Painel: Remessa CNAB (Safra) ─────────────────────────────────────
function SafraRemessaPanel() {
  const [loading, setLoading] = React.useState(true);
  const [titulos, setTitulos] = React.useState([]);
  const [sel, setSel] = React.useState({});
  const [gerando, setGerando] = React.useState(false);
  const [errorBlock, setErrorBlock] = React.useState(null);
  const [remessas, setRemessas] = React.useState([]);
  const [uploadingRetorno, setUploadingRetorno] = React.useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([
        fetch(`${API_URL}/api/dnapay/safra/titulos-elegiveis?limit=200`).then(x => x.json()).catch(() => []),
        fetch(`${API_URL}/api/dnapay/safra/remessas?limit=10`).then(x => x.json()).catch(() => []),
      ]);
      setTitulos(Array.isArray(t) ? t : []);
      setRemessas(Array.isArray(r) ? r : []);
    } finally { setLoading(false); }
  };
  React.useEffect(() => { carregar(); }, []);

  const toggle = (id) => setSel(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleAll = () => {
    const ids = titulos.map(t => t.id);
    const allOn = ids.length > 0 && ids.every(id => sel[id]);
    const next = {};
    if (!allOn) ids.forEach(id => { next[id] = true; });
    setSel(next);
  };
  const selecionados = titulos.filter(t => sel[t.id]);

  const gerarRemessa = async () => {
    if (selecionados.length === 0) { toast.error('Selecione pelo menos um título.'); return; }
    setGerando(true);
    setErrorBlock(null);
    try {
      const r = await fetch(`${API_URL}/api/dnapay/safra/remessa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ titulo_ids: selecionados.map(t => t.id) }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = data?.detail || data;
        const msg = (typeof detail === 'string') ? detail : (detail?.detail || 'Erro ao gerar remessa');
        const missing = detail?.missing_fields || [];
        setErrorBlock({ status: r.status, message: msg, missing });
        toast.error(msg);
        return;
      }
      toast.success(`Remessa #${data.remessa_num} gerada (${data.qtd_titulos} título(s))`);
      // download
      window.open(`${API_URL}${data.download_url}`, '_blank');
      setSel({});
      carregar();
    } catch (e) {
      setErrorBlock({ status: 0, message: e.message });
      toast.error('Falha ao gerar remessa');
    } finally { setGerando(false); }
  };

  const handleRetorno = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingRetorno(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/dnapay/safra/retorno`, { method: 'POST', body: fd });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(data?.detail || 'Falha no retorno'); return; }
      toast.success(`Retorno processado: ${data.qtd_registros} registro(s), ${data.qtd_baixados} baixado(s)`);
      carregar();
    } finally {
      setUploadingRetorno(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-4" data-testid="safra-remessa-panel">
      <Card className="border-purple-100">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-sm text-purple-800">Títulos elegíveis para Remessa CNAB</CardTitle>
            <CardDescription className="text-xs">
              Pendentes, vencidos ou parciais — sem remessa ativa.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <input id="retorno-input" type="file" accept=".ret,.txt,.cnab,application/octet-stream" className="hidden" onChange={handleRetorno} />
            <Button size="sm" variant="outline" onClick={() => document.getElementById('retorno-input').click()} disabled={uploadingRetorno} data-testid="safra-upload-retorno">
              {uploadingRetorno ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Processando…</> : <>Leitura de Retorno</>}
            </Button>
            <Button size="sm" onClick={gerarRemessa} disabled={gerando || selecionados.length === 0}
              className="bg-gradient-to-r from-purple-700 to-emerald-600 text-white"
              data-testid="safra-gerar-remessa">
              {gerando ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Gerando…</> : <>Gerar Remessa CNAB ({selecionados.length})</>}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {errorBlock && (
            <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-xs">
              <div className="font-semibold text-rose-700 mb-1">Erro {errorBlock.status}: {errorBlock.message}</div>
              {errorBlock.missing?.length > 0 && (
                <ul className="list-disc list-inside text-rose-700 space-y-0.5">
                  {errorBlock.missing.map((m, i) => <li key={i} className="font-mono">{m}</li>)}
                </ul>
              )}
            </div>
          )}
          {loading ? (
            <div className="py-6 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Carregando…</div>
          ) : titulos.length === 0 ? (
            <div className="py-6 text-center text-slate-500 text-sm">Nenhum título elegível.</div>
          ) : (
            <DnaTableV4 stickyHeader density="compact" testId="dnapay-config-titulos-table-v4">
              <table className="w-full text-xs">
                <thead className="text-purple-800">
                  <tr>
                    <th className="px-2 py-1.5 text-left"><input type="checkbox" onChange={toggleAll} checked={titulos.length > 0 && titulos.every(t => sel[t.id])} /></th>
                    <th className="px-2 py-1.5 text-left">Aluno / Responsável</th>
                    <th className="px-2 py-1.5 text-left">Descrição</th>
                    <th className="px-2 py-1.5 text-right">Valor</th>
                    <th className="px-2 py-1.5 text-center">Venc.</th>
                    <th className="px-2 py-1.5 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {titulos.map(t => (
                    <tr key={t.id}>
                      <td className="px-2 py-1"><input type="checkbox" checked={!!sel[t.id]} onChange={() => toggle(t.id)} data-testid={`safra-sel-${t.id}`} /></td>
                      <td className="px-2 py-1">
                        <div className="font-semibold">{t.aluno_nome || '—'}</div>
                        <div className="text-slate-500">{t.responsavel_nome || '—'}</div>
                      </td>
                      <td className="px-2 py-1">{t.descricao}</td>
                      <td className="px-2 py-1 text-right font-semibold">R$ {Number(t.valor_final || t.valor_original || 0).toFixed(2)}</td>
                      <td className="px-2 py-1 text-center">{(t.vencimento || '').slice(0, 10)}</td>
                      <td className="px-2 py-1 text-center"><Badge variant="outline" className="text-[10px]">{t.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DnaTableV4>
          )}
        </CardContent>
      </Card>

      {remessas.length > 0 && (
        <Card className="border-emerald-100">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-800">Últimas remessas</CardTitle></CardHeader>
          <CardContent>
            <DnaTableV4 stickyHeader density="compact" testId="dnapay-config-remessas-table-v4">
              <table className="w-full text-xs">
                <thead className="text-emerald-800">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Nº</th>
                    <th className="px-2 py-1.5 text-left">Arquivo</th>
                    <th className="px-2 py-1.5 text-center">Layout</th>
                    <th className="px-2 py-1.5 text-right">Títulos</th>
                    <th className="px-2 py-1.5 text-right">Valor</th>
                    <th className="px-2 py-1.5 text-center">Data</th>
                    <th className="px-2 py-1.5 text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {remessas.map(r => (
                    <tr key={r.id}>
                      <td className="px-2 py-1 font-semibold">#{r.remessa_num}</td>
                      <td className="px-2 py-1 font-mono text-[10px]">{r.filename}</td>
                      <td className="px-2 py-1 text-center">{r.layout}</td>
                      <td className="px-2 py-1 text-right">{r.qtd_titulos}</td>
                      <td className="px-2 py-1 text-right">R$ {Number(r.valor_total || 0).toFixed(2)}</td>
                      <td className="px-2 py-1 text-center">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                      <td className="px-2 py-1 text-center">
                        <a href={`${API_URL}${r.download_url}`} target="_blank" rel="noreferrer" className="text-purple-700 underline">Baixar</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DnaTableV4>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function DnaPayConfig() {
  const [loading, setLoading] = useState(true);
  const [gateways, setGateways] = useState([]);
  const [regras, setRegras] = useState([]);
  const [showConfig, setShowConfig] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [configForm, setConfigForm] = useState({});
  const [showRegra, setShowRegra] = useState(false);
  const [testandoConexao, setTestandoConexao] = useState(false);
  const [statusEmissao, setStatusEmissao] = useState(null);
  const [showSecret, setShowSecret] = useState({});
  const [regraForm, setRegraForm] = useState({
    nome: 'Regra Padrão',
    multa_percentual: 2.0,
    juros_dia_percentual: 0.033,
    dias_tolerancia: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [gatewaysRes, regrasRes] = await Promise.all([
        fetch(`${API_URL}/api/dnapay/pagamentos/gateways/config`),
        fetch(`${API_URL}/api/dnapay/billing/regras`)
      ]);

      if (gatewaysRes.ok) setGateways(await gatewaysRes.json());
      if (regrasRes.ok) setRegras(await regrasRes.json());
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGateway = async () => {
    try {
      const payload = {
        provedor: selectedProvider,
        ambiente: configForm.ambiente || 'sandbox',
        ativo: configForm.ativo || false,
        ...configForm
      };

      const response = await fetch(`${API_URL}/api/dnapay/pagamentos/gateways/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Gateway configurado com sucesso!');
        setShowConfig(false);
        loadData();
      } else {
        toast.error('Erro ao salvar configuração');
      }
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handleSaveRegra = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dnapay/billing/regras`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...regraForm, ativo: true })
      });

      if (response.ok) {
        toast.success('Regra financeira salva!');
        setShowRegra(false);
        loadData();
      } else {
        toast.error('Erro ao salvar regra');
      }
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const testarConexaoSafra = async () => {
    setTestandoConexao(true);
    try {
      const r = await fetch(`${API_URL}/api/dnapay/pagamentos/gateways/config/safra/testar-conexao`, { method: 'POST' });
      const j = await r.json();
      if (j.ok) toast.success(j.message || 'Conexão Safra OK');
      else toast.warning(j.message || 'Configuração incompleta');
    } catch (e) {
      toast.error('Não foi possível testar conexão');
    } finally {
      setTestandoConexao(false);
    }
  };

  const carregarStatusEmissao = async () => {
    try {
      const r = await fetch(`${API_URL}/api/dnapay/pagamentos/gateways/status-emissao`);
      if (r.ok) setStatusEmissao(await r.json());
    } catch { /* ignore */ }
  };

  const getGatewayStatus = (provedor) => {
    const config = gateways.find(g => g.provedor === provedor);
    return config?.ativo ? 'ativo' : 'inativo';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="dnapay-config">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-7 h-7 text-purple-600" />
            Configurações DNA Pay
          </h1>
          <p className="text-gray-500">Configure gateways de pagamento e regras financeiras</p>
        </div>
      </div>

      <Tabs defaultValue="gateways" className="space-y-4">
        <TabsList>
          <TabsTrigger value="gateways">Gateways de Pagamento</TabsTrigger>
          <TabsTrigger value="regras">Regras Financeiras</TabsTrigger>
          <TabsTrigger value="remessa">Remessa CNAB (Safra)</TabsTrigger>
        </TabsList>

        {/* Gateways */}
        <TabsContent value="gateways">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(gatewayInfo).map(([key, info]) => {
              const Icon = info.icon;
              const status = getGatewayStatus(key);
              const config = gateways.find(g => g.provedor === key);
              
              return (
                <Card key={key} className="relative overflow-hidden">
                  <div className={`absolute top-0 left-0 right-0 h-1 ${status === 'ativo' ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${info.color}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{info.name}</CardTitle>
                          <CardDescription>{info.description}</CardDescription>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Status</span>
                      <Badge className={status === 'ativo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                        {status === 'ativo' ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                        {status === 'ativo' ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>
                    
                    {config && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Ambiente</span>
                        <Badge variant="outline" className="capitalize">{config.ambiente || 'sandbox'}</Badge>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1"
                        onClick={() => window.open(info.docs, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Docs
                      </Button>
                      <Button 
                        size="sm"
                        className="flex-1"
                        onClick={() => {
                          setSelectedProvider(key);
                          setConfigForm(config || { ambiente: 'sandbox', ativo: false });
                          if (key === 'safra') carregarStatusEmissao();
                          setShowConfig(true);
                        }}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Configurar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="mt-6 border-yellow-200 bg-yellow-50">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Modo de Desenvolvimento</p>
                <p className="text-sm text-yellow-700">
                  Os gateways estão operando em modo MOCK para desenvolvimento. 
                  Configure as credenciais reais e ative o ambiente de produção quando estiver pronto.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Regras Financeiras */}
        <TabsContent value="regras">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Regras de Cálculo</CardTitle>
                <CardDescription>Configure multas, juros e tolerâncias</CardDescription>
              </div>
              <Button onClick={() => setShowRegra(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nova Regra
              </Button>
            </CardHeader>
            <CardContent>
              {regras.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Nenhuma regra configurada</p>
                  <p className="text-sm">Usando valores padrão (2% multa, 0.033%/dia juros)</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {regras.map((regra) => (
                    <div 
                      key={regra.id}
                      className={`p-4 rounded-lg border ${regra.ativo ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{regra.nome}</h3>
                          {regra.ativo && <Badge className="bg-green-100 text-green-700">Ativa</Badge>}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Multa</p>
                          <p className="font-medium">{regra.multa_percentual}%</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Juros/dia</p>
                          <p className="font-medium">{regra.juros_dia_percentual}%</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Tolerância</p>
                          <p className="font-medium">{regra.dias_tolerancia} dias</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Remessa CNAB Safra */}
        <TabsContent value="remessa">
          <SafraRemessaPanel />
        </TabsContent>
      </Tabs>

      {/* Modal Configurar Gateway */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-purple-700">Configurar {gatewayInfo[selectedProvider]?.name}</span>
              {selectedProvider === 'safra' && configForm.ambiente && (
                <Badge className={configForm.ambiente === 'production' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                  {configForm.ambiente === 'production' ? 'Produção' : 'Homologação'}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedProvider === 'safra'
                ? 'Configuração para emissão real de boletos registrados.'
                : 'Insira as credenciais do gateway de pagamento.'}
            </DialogDescription>
          </DialogHeader>

          {selectedProvider === 'safra' ? (
            <SafraConfigForm
              configForm={configForm}
              setConfigForm={setConfigForm}
              onTestar={testarConexaoSafra}
              testando={testandoConexao}
              statusEmissao={statusEmissao}
            />
          ) : selectedProvider && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Gateway Ativo</Label>
                <Switch
                  checked={configForm.ativo || false}
                  onCheckedChange={(checked) => setConfigForm({...configForm, ativo: checked})}
                />
              </div>
              
              <div>
                <Label>Ambiente</Label>
                <Select 
                  value={configForm.ambiente || 'sandbox'} 
                  onValueChange={(v) => setConfigForm({...configForm, ambiente: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox (Testes)</SelectItem>
                    <SelectItem value="production">Produção</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {gatewayInfo[selectedProvider]?.fields.map((field) => (
                <div key={field}>
                  <Label className="capitalize">{field.replace('_', ' ')}</Label>
                  <Input
                    type={field.includes('secret') || field.includes('token') ? 'password' : 'text'}
                    value={configForm[field] || ''}
                    onChange={(e) => setConfigForm({...configForm, [field]: e.target.value})}
                    placeholder={`Insira o ${field.replace('_', ' ')}`}
                  />
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Cancelar</Button>
            <Button onClick={handleSaveGateway} className="bg-gradient-to-r from-purple-700 to-emerald-600 text-white hover:opacity-90">
              <Save className="w-4 h-4 mr-2" />
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Nova Regra */}
      <Dialog open={showRegra} onOpenChange={setShowRegra}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Regra Financeira</DialogTitle>
            <DialogDescription>
              Configure multas e juros para títulos vencidos
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nome da Regra</Label>
              <Input
                value={regraForm.nome}
                onChange={(e) => setRegraForm({...regraForm, nome: e.target.value})}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Multa (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={regraForm.multa_percentual}
                  onChange={(e) => setRegraForm({...regraForm, multa_percentual: parseFloat(e.target.value)})}
                />
              </div>
              <div>
                <Label>Juros ao dia (%)</Label>
                <Input
                  type="number"
                  step="0.001"
                  value={regraForm.juros_dia_percentual}
                  onChange={(e) => setRegraForm({...regraForm, juros_dia_percentual: parseFloat(e.target.value)})}
                />
              </div>
            </div>
            
            <div>
              <Label>Dias de Tolerância</Label>
              <Input
                type="number"
                value={regraForm.dias_tolerancia}
                onChange={(e) => setRegraForm({...regraForm, dias_tolerancia: parseInt(e.target.value)})}
              />
              <p className="text-xs text-gray-500 mt-1">Dias após vencimento sem cobrar encargos</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegra(false)}>Cancelar</Button>
            <Button onClick={handleSaveRegra}>Salvar Regra</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
